<template>
    <PlanbordGridCell
        class="headerCell"
        header
        centeredVertically
        :rosterEnded="day.ended"
        :isProposed="day.proposed"
        :isPublished="day.published"
        :workloadScheduleFulfillmentStatus="workloadScheduleFulfillmentStatus"
        :inConstruction="!day.ended && !day.proposed && !day.published"
        :publishedWithoutRights="day.published && rightsCantBeDerived"
        :data-x="index"
        :title="headerTitle"
    >
        <a :href="secondaryPageLink" target="_blank" @click="emitSecondaryPageLink">
            <span class="date-marker">
                {{ day.dayAbbreviation }} {{ day.formattedDateOnly }}
            </span>
            <span
                v-if="holidayDescriptions"
                :title="holidayDescriptions"
                class="holiday-marker mdi mdi-beach holiday-title"
            > {{ holidayDescriptions }}</span>
            <span v-if="shouldShowUnpublishedPartVisibleBadge" title="Rooster wordt getoond, alleen zicht" class="unpublished-visible">👁</span>
        </a>
    </PlanbordGridCell>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import HolidayUtil from '@/util/holidayutil';
    import {RosterDay, RosterData} from '@/types/types';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import {PlanbordWorkSchedulePosition} from '@/types/api-object-extensions';
    import RoosterplanningUrlBuilder from '@/util/RoosterplanningUrlBuilder';

    @Component({
        components: {
            PlanbordGridCell,
        },
        emits: ['open:workSchedule'],
    })
    export default class PlanbordHeaderCell extends Vue {
        @Prop({required: true}) public day!: RosterDay;
        @Prop({type: Number, required: true}) public index!: number;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({type: Boolean}) public showCompactMode!: boolean;
        @Prop({type: Boolean}) public enabledWorkSchedule!: boolean;
        @Prop() public workSchedulePositions?: PlanbordWorkSchedulePosition[];

        public get shouldShowUnpublishedPartVisibleBadge(): boolean {
            if (!this.roster.unpublishedPartVisibleUntil || (this.roster.publishedUntil && (this.roster.publishedUntil >= this.day.date))) {
                return false;
            }

            return this.roster.unpublishedPartVisibleUntil >= this.day.date;
        }

        public get workloadScheduleFulfillmentStatus(): 'full' | 'partial' | 'none' | undefined {
            if (!this.enabledWorkSchedule || this.workSchedulePositions === undefined) {
                return undefined;
            }

            if (this.workSchedulePositions.length === 0) {
                return 'full';
            }

            let totalPositionTime: number = 0;
            let totalAssignmentTime: number = 0;

            for (const position of this.workSchedulePositions) {
                totalPositionTime += position.endDateTime.getTime() - position.beginDateTime.getTime();

                for (const assignment of position.workScheduleAssignments!) {
                    totalAssignmentTime += assignment.endDateTime.getTime() - assignment.beginDateTime.getTime();
                }
            }

            if (totalAssignmentTime >= totalPositionTime) {
                return 'full';
            }

            if (totalAssignmentTime > 0) {
                return 'partial';
            }

            return 'none';
        }

        public emitSecondaryPageLink(event: Event): void {
            if (this.enabledWorkSchedule && this.secondaryPageLinkEnabled && this.roster.secondaryPage!.type === 'work-schedule' && this.day.published) {
                event.preventDefault();
                this.$emit('open:workSchedule', this.day);
            }
        }

        public get holidayDescriptions(): string {
            return HolidayUtil.getHolidayNamesAsSingleString(this.day, this.roster.holidays);
        }

        public get secondaryPageLinkEnabled(): boolean {
            return (!!this.roster.secondaryPage
                && this.roster.secondaryPage.type !== 'none'
                && this.roster.secondaryPage.beginDate <= this.day.date
                && (!this.roster.secondaryPage.endDate || this.roster.secondaryPage.endDate >= this.day.date)
            ) || false;
        }

        public get secondaryPageLink(): string | undefined {
            if (!this.secondaryPageLinkEnabled) {
                return undefined;
            }

            if (this.roster.secondaryPage!.type === 'work-schedule') {
                return this.day.published ? '#' : undefined;
            }

            if (this.roster.secondaryPage!.type === 'position-assignment') {
                return `${RoosterplanningUrlBuilder.getHostUrl()}/page/position-assignment?rosterId=${this.roster.rosterId}&date=${this.unixTimestamp}&returnTo=planbord`;
            }

            const action: string = this.roster.secondaryPage!.type === 'day-list' ? 'daylist' : 'formationfill';

            return `${RoosterplanningUrlBuilder.getHostUrl()}?pageid=P_ROOSTERWEB_COMP_OVV&rosterid=${this.roster.rosterId}&action=${action}&begindate=${this.beginDateUnixTimestamp}&date=${this.unixTimestamp}`;
        }

        public get unixTimestamp(): string {
            return (this.day.date.valueOf() / 1000).toFixed();
        }

        public get beginDateUnixTimestamp(): string {
            return (this.roster.days[0].date.valueOf() / 1000).toFixed();
        }

        public get rightsCantBeDerived(): boolean {
            return !!this.roster.deriveRightsUntil && this.roster.deriveRightsUntil < this.day.date;
        }

        public get headerTitle(): string {
            if (this.day.published) {
                if (this.rightsCantBeDerived) {
                    return 'Actief, nog niet definitief rooster. Er kunnen geen rechten aan worden ontleend.';
                }

                return 'Actief rooster';
            }

            if (this.shouldShowUnpublishedPartVisibleBadge) {
                return 'In ontwikkeling met inzage';
            }

            return 'In ontwikkeling';
        }
    }
</script>

<style lang="scss" scoped>
    .date-marker {
        display: block;
        background: none;
        color: var(--theme-text-color);
        padding: 0.2rem;
        border-radius: 0.625rem;

        &.active {
            color: var(--theme-text-color);
            background: var(--active-marker-background);
        }
    }

    .holiday-marker {
        display: block;
        border-radius: 10px;
        color: var(--annotation-text-color);
        background-color: var(--annotation-background);
        padding: 0.125rem 0.3125rem;
    }

    .compact {
        // semi-cross component, the .compact class is applied on a parent component (TODO: replace with a theme where spacing is defined to be smaller in the theme)
        .holiday-marker {
            display: none;
        }

        .headerCell:hover {
            .holiday-marker {
                display: block;
                position: absolute;
                transform: translate(-25%, 1.5rem);
            }
        }
    }

    a {
        text-decoration: none;
        color: var(--theme-text-color);
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    span.unpublished-visible {
        display: inline-flex;
        background: var(--construction-header-color);
        width: 1.375rem;
        height: 1.375rem;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        font-size: 1.25rem;
        padding-bottom: 0.25rem;
        margin-bottom: 0.3rem;
    }
</style>
